import { OffsetOption } from '@shared/core/custom-offsets'
import type { SupportedLanguage } from '@shared/core/localized-defaults'
import { MessageType } from '@shared/core/reminders'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { Control, Controller, FieldErrors, UseFormRegister, useWatch } from 'react-hook-form'
import { FormControl, InfoModal, ToggleSwitch, Typography } from 'src/modules/core/components'
import { MessageInput } from 'src/modules/core/components/message-input/message-input'
import classes from './reminder-type-section.module.scss'

interface ReminderTypeSectionProps {
  type: MessageType
  title: string
  description: string
  checked: boolean
  disabled?: boolean
  messageField: string
  messageFieldPlaceholder: string
  messageLanguage?: SupportedLanguage
  offsetsField: string
  timingFieldLabel: string
  register: UseFormRegister<any>
  control: Control<any>
  errors: FieldErrors<any>
  messagePreview: string
  messageLength: number
  segmentsCount: number
  timingOptions: OffsetOption[]
  confirmationsEnabled?: boolean
  onToggle?: (enabled: boolean) => void
  onAddOffset?: (offsetInMs: number, type: MessageType) => Promise<void>
  onRemoveOffset?: (id: string, offsetInMs: number, type: MessageType) => void
}

export function ReminderTypeSection({
  type,
  title,
  description,
  checked,
  disabled,
  messageField,
  messageFieldPlaceholder,
  messageLanguage,
  offsetsField,
  timingFieldLabel,
  register,
  control,
  errors,
  messagePreview,
  messageLength,
  segmentsCount,
  timingOptions,
  confirmationsEnabled,
  onToggle,
  onAddOffset,
  onRemoveOffset,
}: ReminderTypeSectionProps) {
  const messageTemplate = useWatch({ control, name: messageField })
  const [isOpen, setIsOpen] = useState(false)

  // Custom offset state
  const [isAddingCustomOffset, setIsAddingCustomOffset] = useState(false)
  const [customDays, setCustomDays] = useState('0')
  const [customHours, setCustomHours] = useState('0')
  const [customMinutes, setCustomMinutes] = useState('0')
  const [customOffsetError, setCustomOffsetError] = useState<string | null>(null)
  const [isSubmittingOffset, setIsSubmittingOffset] = useState(false)

  // Remove offset confirmation state
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false)
  const [offsetToRemove, setOffsetToRemove] = useState<{
    id: string
    offsetInMs: number
    type: MessageType
    label: string
  } | null>(null)

  // Ref for the days input field
  const daysInputRef = useRef<HTMLInputElement>(null)

  // Focus the days input when custom offset UI opens
  useEffect(() => {
    if (isAddingCustomOffset && daysInputRef.current) {
      daysInputRef.current.focus()
    }
  }, [isAddingCustomOffset])

  // Clear error when user starts typing
  const clearError = () => {
    if (customOffsetError) {
      setCustomOffsetError(null)
    }
  }

  // Handle custom offset actions
  // This function now relies on backend validation and displays any errors returned
  const handleSaveCustomOffset = async () => {
    // Calculate total milliseconds
    const days = parseInt(customDays) || 0
    const hours = parseInt(customHours) || 0
    const minutes = parseInt(customMinutes) || 0
    const totalMs = (days * 24 * 60 * 60 + hours * 60 * 60 + minutes * 60) * 1000

    // Clear previous errors
    clearError()

    // Only proceed if we have a positive value
    if (totalMs > 0 && onAddOffset) {
      setIsSubmittingOffset(true)
      try {
        await onAddOffset(totalMs, type)
        // Success - close the form
        setIsAddingCustomOffset(false)
        setCustomDays('0')
        setCustomHours('0')
        setCustomMinutes('0')
      } catch (error) {
        // Handle API errors from backend validation
        const errorMessage = error instanceof Error ? error.message : 'Failed to add custom timing'
        setCustomOffsetError(errorMessage)
      } finally {
        setIsSubmittingOffset(false)
      }
    }
  }

  const handleCancelCustomOffset = () => {
    setIsAddingCustomOffset(false)
    setCustomDays('0')
    setCustomHours('0')
    setCustomMinutes('0')
    clearError()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      handleSaveCustomOffset()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      handleCancelCustomOffset()
    }
  }

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation()
    onToggle?.(e.target.checked)
  }

  const handleAccordionClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    // force open when there are errors
    setIsOpen(!isOpen || errors[messageField]?.message)
  }

  const handleRemoveOffsetClick = (e: React.MouseEvent, id: string, offsetInMs: number, label: string) => {
    e.stopPropagation()
    setOffsetToRemove({ id, offsetInMs, type, label })
    setShowRemoveConfirmModal(true)
  }

  const handleConfirmRemoveOffset = () => {
    if (offsetToRemove && onRemoveOffset) {
      onRemoveOffset(offsetToRemove.id, offsetToRemove.offsetInMs, offsetToRemove.type)
    }
    setShowRemoveConfirmModal(false)
    setOffsetToRemove(null)
  }

  const handleCancelRemoveOffset = () => {
    setShowRemoveConfirmModal(false)
    setOffsetToRemove(null)
  }

  useEffect(() => {
    setIsOpen(checked)
  }, [checked])

  useEffect(() => {
    if (errors[messageField]?.message) {
      setIsOpen(true)
    }
  }, [errors, messageField])

  return (
    <div className={classNames(classes.reminderTypeContainer, { [classes.open]: isOpen })}>
      <div className={classes.reminderTypeHeader} onClick={handleAccordionClick}>
        <ToggleSwitch
          id={messageField}
          labelClassName={classes.switchLabel}
          checked={checked || false}
          disabled={disabled}
          onChange={handleToggle}
        />
        <div className={classes.reminderTypeHeaderText} style={{ cursor: 'pointer' }}>
          <Typography variant="h4" className={classes.reminderTypeTitle}>
            {title}
          </Typography>
          <Typography variant="body" className={classes.reminderTypeDescription}>
            {description}
          </Typography>
        </div>
      </div>
      <div
        className={`${classes.reminderTypeContent} ${isOpen ? classes.open : classes.closed}`}
        style={{
          maxHeight: isOpen ? '1000px' : '0',
          overflow: isOpen ? 'visible' : 'hidden',
        }}
      >
        <MessageInput
          label="Message"
          rows={3}
          {...register(messageField)}
          error={errors[messageField]?.message}
          showDynamicDataHelper={true}
          messageLanguage={messageLanguage}
          confirmationsEnabled={type === 'reminder' && confirmationsEnabled}
          messagePreview={messagePreview}
          messageTemplate={messageTemplate}
          messageLength={messageLength}
          segmentsCount={segmentsCount}
          placeholder={messageFieldPlaceholder}
          disabled={disabled}
        />
        <FormControl label={timingFieldLabel} error={(errors?.[offsetsField] as any)?.message}>
          <Controller
            control={control}
            defaultValue={[]}
            name={offsetsField}
            render={({ field: { onChange, value = [], ref } }) => {
              return (
                <>
                  <div ref={ref} className={classes.timingCheckboxGroup}>
                    {timingOptions.map((option) => {
                      const checked = value.includes(+option.value)
                      return (
                        <label key={option.value} className={classes.timingCheckbox}>
                          <input
                            type="checkbox"
                            value={option.value}
                            checked={checked}
                            disabled={disabled}
                            onChange={(e) => {
                              if (e.target.checked) {
                                onChange([...value, +option.value])
                              } else {
                                onChange(value.filter((v) => v !== +option.value))
                              }
                            }}
                          />
                          {option.label}
                          {option.is_custom && option.id && onRemoveOffset && (
                            <button
                              type="button"
                              className={classes.removeCustomOffsetBtn}
                              title="Remove custom timing"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRemoveOffsetClick(e, option.id, +option.value, option.label)
                              }}
                              disabled={disabled}
                            >
                              ×
                            </button>
                          )}
                        </label>
                      )
                    })}
                  </div>
                  {/* Add custom offset UI */}
                  {!isAddingCustomOffset && (
                    <div className={classes.customOffsetLinkRow}>
                      <button
                        type="button"
                        className={classes.linkButton}
                        onClick={() => setIsAddingCustomOffset(true)}
                        disabled={disabled}
                      >
                        Add a custom timing
                      </button>
                    </div>
                  )}
                  {isAddingCustomOffset && (
                    <div className={classes.customOffsetInputs}>
                      <div className={classes.customOffsetInputRow}>
                        <div className={classes.customOffsetFieldGroup}>
                          <input
                            type="number"
                            min="0"
                            max="365"
                            className={classes.customOffsetInputField}
                            value={customDays}
                            onChange={(e) => {
                              clearError()
                              let val = e.target.value.replace(/[^\d]/g, '')
                              if (val) val = Math.max(0, Math.min(365, +val)).toString()
                              setCustomDays(val)
                            }}
                            onFocus={(e) => e.target.select()}
                            onKeyDown={handleKeyDown}
                            placeholder="0"
                            disabled={disabled || isSubmittingOffset}
                            ref={daysInputRef}
                          />
                          <span>days</span>
                        </div>
                        <div className={classes.customOffsetFieldGroup}>
                          <input
                            type="number"
                            min="0"
                            max="999"
                            className={classes.customOffsetInputField}
                            value={customHours}
                            onChange={(e) => {
                              clearError()
                              let val = e.target.value.replace(/[^\d]/g, '')
                              if (val) val = Math.max(0, Math.min(999, +val)).toString()
                              setCustomHours(val)
                            }}
                            onFocus={(e) => e.target.select()}
                            onKeyDown={handleKeyDown}
                            placeholder="0"
                            disabled={disabled || isSubmittingOffset}
                          />
                          <span>hours</span>
                        </div>
                        <div className={classes.customOffsetFieldGroup}>
                          <input
                            type="number"
                            min="0"
                            max="999"
                            className={classes.customOffsetInputField}
                            value={customMinutes}
                            onChange={(e) => {
                              clearError()
                              let val = e.target.value.replace(/[^\d]/g, '')
                              if (val) val = Math.max(0, Math.min(999, +val)).toString()
                              setCustomMinutes(val)
                            }}
                            onFocus={(e) => e.target.select()}
                            onKeyDown={handleKeyDown}
                            placeholder="0"
                            disabled={disabled || isSubmittingOffset}
                          />
                          <span>minutes</span>
                        </div>
                        <div className={classes.customOffsetButtons}>
                          <button
                            type="button"
                            className={classes.linkButton}
                            onClick={handleSaveCustomOffset}
                            disabled={disabled || isSubmittingOffset}
                          >
                            {isSubmittingOffset ? 'saving...' : 'save'}
                          </button>
                          <button
                            type="button"
                            className={classes.linkButton}
                            onClick={handleCancelCustomOffset}
                            disabled={disabled || isSubmittingOffset}
                          >
                            cancel
                          </button>
                        </div>
                      </div>
                      {customOffsetError && (
                        <div>
                          <Typography variant="body" className={classes.errorText}>
                            {customOffsetError}
                          </Typography>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )
            }}
          />
        </FormControl>
      </div>

      {showRemoveConfirmModal && (
        <InfoModal
          title="Remove Custom Timing?"
          description="Any messages scheduled with this timing will be canceled. This action cannot be undone."
          button="Remove"
          closeButton="Cancel"
          onClose={handleCancelRemoveOffset}
          onClick={handleConfirmRemoveOffset}
        />
      )}
    </div>
  )
}
